import type { ReactNode } from 'react';
import { ApiRefusal, ApiUnreachable } from '@gatecheckpro/api-client';
import {
  asSeatingConfigurationRefusalReason,
  asVenueMapRefusalReason,
  type SeatingConfigurationRefusalReason,
  type VenueMapRefusalReason,
  asRefundRefusalReason,
  type RefundRefusalReason,
} from '@gatecheckpro/contracts';
import { Notice } from '@gatecheckpro/design-system';

/**
 * Turns a thrown failure into something a person can act on.
 *
 * Three things must stay distinguishable, because the right next move differs
 * for each: the network did not reach us, the server refused deliberately, or
 * something broke. Collapsing them into "Something went wrong" is how a product
 * teaches its users that its error messages are not worth reading.
 */
const REMEDY: Readonly<Record<string, string>> = {
  AUTHZ_MFA_REQUIRED: 'Set up an authenticator app under Security, then sign in again to use this.',
  AUTHZ_CAPABILITY_DENIED: 'Ask an owner or administrator of this organization for the role.',
  AUTHZ_SELF_DEMOTION_DENIED: 'Another owner has to make this change for you.',
  MEMBERSHIP_STATE_INVALID: 'Grant someone else the owner role first.',
  ORGANIZATION_SLUG_TAKEN: 'Choose a different workspace address.',
  MEMBERSHIP_ALREADY_EXISTS: 'That person already has a membership or an open invitation.',
  INVITATION_STATE_INVALID: 'Ask for a fresh invitation.',
  ORGANIZATION_VERIFICATION_STATE_INVALID:
    'Verification has already moved on. Reload to see where it stands.',
  IDEMPOTENCY_REQUEST_IN_FLIGHT: 'The previous attempt is still running. Give it a moment.',
  AUTH_RATE_LIMITED: 'Too many attempts. Wait a minute and try again.',
};

/**
 * Venue map refusals, keyed on the server's typed reason rather than its prose.
 *
 * These all arrive as one HTTP conflict, so branching on `code` alone would put
 * the same sentence under four different problems. The reason code is what
 * distinguishes them, and it exists precisely so a client never has to read the
 * message to decide what to offer (ADR 0036).
 */
const VENUE_MAP_REMEDY: Readonly<Record<VenueMapRefusalReason, string>> = {
  VENUE_MAP_OPEN_DRAFT_EXISTS:
    'This map already has a draft open. Approve it or discard it, then start the next one.',
  VENUE_MAP_RETIRED: 'This map is retired. Draft against an active map instead.',
  VENUE_MAP_VERSION_NOT_DRAFT:
    'This version has been approved and cannot change. Start a new draft from it.',
  VENUE_MAP_VERSION_HAS_NO_SEATS: 'Draw at least one seat before approving this version.',
};

/**
 * Seating configuration refusals, keyed on the server's typed reason.
 *
 * Eleven reasons and eleven remedies, because each one sends an organizer
 * somewhere different — and three of them are refusals the product is *right*
 * to make on a night that is already selling, so the sentence has to explain
 * why rather than sound like a fault.
 */
const SEATING_REMEDY: Readonly<Record<SeatingConfigurationRefusalReason, string>> = {
  SEATING_MAP_VERSION_NOT_PUBLISHED:
    'A night can only be pinned to a room that has stopped changing. Approve the map version first.',
  SEATING_MAP_VERSION_PINNED:
    'This session is already set up against an earlier map version and keeps it. Moving a night to a newer room needs a migration, which this release does not have.',
  SEATING_CONFIGURATION_NOT_DRAFT:
    'This configuration is live or replaced and cannot change. Open a new draft version.',
  SEATING_OPEN_DRAFT_EXISTS:
    'This session already has a draft open. Activate it or discard it, then start the next one.',
  SEATING_TABLE_MODE_CONFLICT:
    'A table is sold whole or by the seat, never both. Change the table’s mode, or clear the seat-level setup on it.',
  SEATING_SEAT_NOT_IN_MAP_VERSION:
    'A seat or table here is not part of the pinned map version. Reload the configuration to see the room it is actually pinned to.',
  SEATING_INVENTORY_NOT_FOR_SESSION:
    'That inventory belongs to a different night. Choose inventory provisioned for this session, or provision some.',
  SEATING_ACQUISITION_SPANS_ALLOCATIONS:
    'Those seats draw on different inventory. Configure or acquire each group on its own.',
  SEATING_ALLOCATION_CAPACITY_MISMATCH:
    'The number of chairs on sale and the inventory set aside for them disagree. Reconcile the two before putting this night on sale.',
  SEATING_PRODUCT_NOT_SINGLE_SESSION:
    'A reserved-seat product must admit to this session and no other. Create a product for this night, even if the name and price are the same as another night’s.',
  SEATING_PRODUCT_ALREADY_BOUND_TO_SESSION:
    'That product is already the reserved-seat product for another night. Each night needs its own, even where the name and price match.',
  SEATING_PRODUCT_HAS_NO_APPLICABLE_VERSION:
    'That product has no published terms to freeze yet. Publish it, then configure seats against it.',
  SEATING_INVENTORY_ALREADY_PROVISIONED:
    'This night already has capacity set aside for that product, on different terms. The existing one stands — reload to see it.',
  SEATING_SELLABLE_REQUIRES_INVENTORY:
    'A seat or table set to go on sale needs capacity to draw on. Choose a product with capacity set aside for this night, or keep it off sale.',
  SEATING_SEAT_DETAIL_TOO_LARGE:
    'Too many seats were asked about at once. Zoom in, or work through the room a stand at a time.',
  SEATING_CONFIGURATION_HAS_NO_OFFERS:
    'Nothing here is set to go on sale yet. Configure at least one seat or table before activating.',
  SEATING_LIVE_HOLDS_PRESENT:
    'Somebody is part-way through buying a seat on this configuration. Wait for those to finish or lapse.',
  SEATING_COMMITMENTS_PRESENT:
    'Seats have already been sold against this session, so its setup can no longer be replaced.',
};

function seatingRemedy(error: ApiRefusal): string | null {
  const reason = asSeatingConfigurationRefusalReason(error.reasonCode);
  return reason === null ? null : SEATING_REMEDY[reason];
}

/**
 * A conditional write that lost.
 *
 * Keyed on the code rather than on 412, because a status is a category and this
 * is a specific event. Both stale codes are top-level rather than state-conflict
 * reasons, for the same reason: losing a race is not a fact about the thing
 * being edited, it is a fact about the write.
 */
const STALE_REMEDY: Readonly<Record<string, string>> = {
  SEATING_CONFIGURATION_STALE:
    'Somebody else saved this seating while you were working. Reload to see theirs before deciding what to keep.',
  VENUE_MAP_TOPOLOGY_STALE:
    'Somebody else saved this room while you were working. Reload to see theirs before deciding what to keep.',
};

/**
 * Refund refusals, keyed on the typed reason rather than the error code.
 *
 * Every refund refusal arrives as `REFUND_REFUSED`, so a map keyed on the code
 * could only ever hold one sentence for a dozen different situations. The
 * reason is the thing that differs — and the difference matters: "this order
 * was free" and "this provider cannot pay it back" lead to completely different
 * actions, and only one of them is fixable from this screen.
 */
const REFUND_REMEDY: Readonly<Record<RefundRefusalReason, string>> = {
  // Refunds. Each says what the organizer can actually do next, because the
  // difference between "this order was free" and "this provider cannot pay it
  // back" leads to completely different actions and only one of them is fixable
  // from this screen.
  IDEMPOTENCY_KEY_REUSED:
    'That reference was already used for a different refund. Start again rather than reusing it.',
  ORDER_NOT_PAID: 'Nothing was collected for this order, so there is nothing to give back.',
  ORDER_WAS_FREE:
    'This order was free. There was no payment to reverse — cancelling the tickets is a ' +
    'different action.',
  REFUND_EXCEEDS_COLLECTION:
    'That is more than was collected once the refunds already raised on this order are counted.',
  REFUND_LINES_DO_NOT_BALANCE: 'The per-ticket amounts do not add up to the total.',
  TICKET_NOT_ON_ORDER: 'One of those tickets belongs to a different order.',
  TICKET_ALREADY_VOID: 'One of those tickets has already been cancelled.',
  TICKET_ALREADY_REFUNDED: 'One of those tickets is already covered by a live refund.',
  REFUND_NOT_PENDING_DECISION: 'This refund has already been decided. Reload to see where it is.',
  REFUND_ALREADY_DECIDED: 'Somebody decided this refund already, and differently.',
  NO_PAYMENT_PROVIDER_CONFIGURED:
    'No payment provider is connected in this deployment, so a refund cannot be sent. The ' +
    'tickets stay cancelled.',
  PROVIDER_CANNOT_REFUND:
    'The connected provider cannot return this payment. The tickets stay cancelled and the ' +
    'money has to be returned another way — this is not something to retry here.',
};

function refundRemedy(error: ApiRefusal): string | null {
  const reason = asRefundRefusalReason(error.reasonCode);
  return reason === null ? null : REFUND_REMEDY[reason];
}

function staleRemedy(error: ApiRefusal): string | null {
  return STALE_REMEDY[error.code] ?? null;
}

function venueMapRemedy(error: ApiRefusal): string | null {
  const reason = asVenueMapRefusalReason(error.reasonCode);
  return reason === null ? null : VENUE_MAP_REMEDY[reason];
}

export function describeFailure(error: unknown): {
  tone: 'critical' | 'caution';
  title: string;
  message: string;
  detail: string | null;
} {
  if (error instanceof ApiUnreachable) {
    return {
      tone: 'caution',
      title: 'Could not reach GateCheckPro',
      message: 'Nothing was changed. Check the connection and try again.',
      detail: null,
    };
  }
  if (error instanceof ApiRefusal) {
    return {
      tone: error.status >= 500 ? 'critical' : 'caution',
      title: error.status >= 500 ? 'GateCheckPro could not complete that' : 'That was refused',
      message: error.message,
      // A typed reason first, then the code, then the request id support
      // correlates against the audit trail. Prose is never parsed: a message is
      // for a human and may be reworded at any time.
      detail:
        venueMapRemedy(error) ??
        seatingRemedy(error) ??
        staleRemedy(error) ??
        refundRemedy(error) ??
        REMEDY[error.code] ??
        (error.requestId === '' ? null : `Reference ${error.requestId}`),
    };
  }
  return {
    tone: 'critical',
    title: 'Something went wrong',
    message: 'The workspace hit an unexpected problem. Reload and try again.',
    detail: null,
  };
}

export function FailureNotice({ error }: { readonly error: unknown }): ReactNode {
  if (error === null || error === undefined) return null;
  const described = describeFailure(error);
  return (
    <Notice tone={described.tone} title={described.title}>
      {described.message}
      {described.detail !== null && (
        <div className="gc-muted" style={{ marginTop: '0.25rem' }}>
          {described.detail}
        </div>
      )}
    </Notice>
  );
}
